/**
 * Google Maps JS SDK loader (F-FE-N26).
 *
 * Singleton script-tag injector: the SDK is never in the app bundle and only
 * loads on demand (the Delivery Zones editor). Re-invocations — including
 * remounts of the page — reuse the same in-flight promise, so the script tag
 * is injected at most once per session. A key change (admin pasted a new key)
 * requires a reload; we surface that instead of double-injecting.
 *
 * This is the plain <script> loader (no npm runtime dep) — the sanctioned,
 * TOS-clean way to consume Maps JS. Types come from @types/google.maps.
 */

let loading: Promise<typeof google.maps> | null = null
let loadedKey: string | null = null

export function loadGoogleMaps(apiKey: string): Promise<typeof google.maps> {
  const key = apiKey.trim()
  if (!key) return Promise.reject(new Error('Google Maps API key is not configured'))
  if (loading) {
    if (loadedKey !== key) {
      return Promise.reject(
        new Error('Google Maps was already loaded with a different API key — reload the page to apply the new key.'),
      )
    }
    return loading
  }
  loadedKey = key
  loading = new Promise((resolve, reject) => {
    if (typeof google !== 'undefined' && google.maps?.Map) {
      resolve(google.maps)
      return
    }
    const cbName = '__posGmapsLoaded'
    ;(window as any)[cbName] = () => {
      delete (window as any)[cbName]
      resolve(google.maps)
    }
    const script = document.createElement('script')
    // NOTE: no `drawing` library — Google removed DrawingManager from Maps
    // JS v3.65+; the zones editor draws polygons manually (map clicks).
    script.src =
      `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}` +
      `&libraries=places,marker&loading=async&callback=${cbName}`
    script.async = true
    script.onerror = () => {
      loading = null
      loadedKey = null
      reject(new Error('Failed to load Google Maps — check the API key and your connection.'))
    }
    document.head.appendChild(script)
  })
  return loading
}
